import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../lib/i18n';
import { budgetsAPI, categoriesAPI, budgetHistoryAPI } from '../lib/api';
import { formatCurrency, cn } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { Plus, Trash2, Edit2, RefreshCw, AlertTriangle, CheckCircle, Target, BarChart3, TrendingUp, TrendingDown } from 'lucide-react';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Line, ComposedChart } from 'recharts';

const Budgets = () => {
  const { user, language } = useAuth();
  const t = useTranslation(language);
  
  const [budgets, setBudgets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBudget, setEditingBudget] = useState(null);
  const [selectedBudgetHistory, setSelectedBudgetHistory] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  
  const [form, setForm] = useState({
    category_id: '',
    amount: '',
    period: 'monthly'
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [budgetsRes, categoriesRes] = await Promise.all([
        budgetsAPI.getAll(),
        categoriesAPI.getAll()
      ]);
      setBudgets(budgetsRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      toast.error('Error al cargar presupuestos');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async (budgetId) => {
    setHistoryLoading(true);
    try {
      const response = await budgetHistoryAPI.getHistory(budgetId, 6);
      setHistoryData(response.data);
      setSelectedBudgetHistory(budgetId);
    } catch (error) {
      toast.error('Error al cargar historial');
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Check alerts on load
    budgetsAPI.checkAlerts().catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.category_id || !form.amount) {
      toast.error('Completa todos los campos');
      return;
    }

    try {
      const payload = {
        category_id: form.category_id,
        amount: parseFloat(form.amount),
        period: form.period
      };

      if (editingBudget) {
        await budgetsAPI.update(editingBudget.id, payload);
        toast.success('Presupuesto actualizado');
      } else {
        await budgetsAPI.create(payload);
        toast.success('Presupuesto creado');
      }
      
      setDialogOpen(false);
      setEditingBudget(null);
      setForm({ category_id: '', amount: '', period: 'monthly' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este presupuesto?')) return;
    
    try {
      await budgetsAPI.delete(id);
      toast.success('Presupuesto eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleEdit = (budget) => {
    setEditingBudget(budget);
    setForm({
      category_id: budget.category_id,
      amount: budget.amount.toString(),
      period: budget.period
    });
    setDialogOpen(true);
  };

  const openNewDialog = () => {
    setEditingBudget(null);
    setForm({ category_id: '', amount: '', period: 'monthly' });
    setDialogOpen(true);
  };

  // Get categories without budgets
  const availableCategories = categories.filter(cat => 
    editingBudget?.category_id === cat.id || !budgets.some(b => b.category_id === cat.id)
  );

  const totalBudgeted = budgets.reduce((sum, b) => sum + b.amount, 0);
  const totalSpent = budgets.reduce((sum, b) => sum + b.spent, 0);
  const exceededCount = budgets.filter(b => b.is_exceeded).length;

  return (
    <div className="space-y-6" data-testid="budgets-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display font-bold text-2xl lg:text-3xl text-slate-900 tracking-tight">
            Presupuestos
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Control de gastos por categoría
          </p>
        </div>
        <Button 
          onClick={openNewDialog}
          className="bg-emerald-900 hover:bg-emerald-800 rounded-sm"
          data-testid="new-budget-btn"
        >
          <Plus size={16} className="mr-2" />
          Nuevo Presupuesto
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-slate-200 rounded-sm shadow-none">
          <CardContent className="p-6">
            <p className="text-xs uppercase tracking-widest font-semibold text-slate-400 mb-2">
              Total Presupuestado
            </p>
            <p className="font-mono text-2xl font-medium text-slate-900">
              {formatCurrency(totalBudgeted, user?.currency)}
            </p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 rounded-sm shadow-none">
          <CardContent className="p-6">
            <p className="text-xs uppercase tracking-widest font-semibold text-slate-400 mb-2">
              Total Gastado
            </p>
            <p className="font-mono text-2xl font-medium text-slate-900">
              {formatCurrency(totalSpent, user?.currency)}
            </p>
          </CardContent>
        </Card>
        <Card className={cn(
          "border-slate-200 rounded-sm shadow-none",
          exceededCount > 0 && "border-red-200 bg-red-50"
        )}>
          <CardContent className="p-6">
            <p className="text-xs uppercase tracking-widest font-semibold text-slate-400 mb-2">
              Presupuestos Excedidos
            </p>
            <p className={cn(
              "font-mono text-2xl font-medium",
              exceededCount > 0 ? "text-red-500" : "text-emerald-600"
            )}>
              {exceededCount} de {budgets.length}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Budgets List */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <RefreshCw className="w-6 h-6 animate-spin text-emerald-900" />
        </div>
      ) : budgets.length === 0 ? (
        <Card className="border-slate-200 rounded-sm shadow-none">
          <CardContent className="flex flex-col items-center justify-center h-48 text-slate-400">
            <Target size={32} className="mb-2" />
            <p>No hay presupuestos configurados</p>
            <Button variant="link" onClick={openNewDialog} className="text-emerald-900">
              Crear primer presupuesto
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {budgets.map(budget => (
            <Card 
              key={budget.id}
              className={cn(
                "border-slate-200 rounded-sm shadow-none transition-colors",
                budget.is_exceeded ? "border-red-300 bg-red-50/50" : "hover:border-emerald-900/30"
              )}
              data-testid={`budget-${budget.id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-10 h-10 rounded-sm flex items-center justify-center"
                      style={{ backgroundColor: `${budget.category_color}15` }}
                    >
                      {budget.is_exceeded ? (
                        <AlertTriangle className="w-5 h-5 text-red-500" strokeWidth={1.5} />
                      ) : budget.percentage >= 100 ? (
                        <CheckCircle className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
                      ) : (
                        <Target className="w-5 h-5" style={{ color: budget.category_color }} strokeWidth={1.5} />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{budget.category_name}</p>
                      <p className="text-xs text-slate-400 capitalize">{budget.period}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleEdit(budget)}
                      className="h-8 w-8 p-0 text-slate-400 hover:text-emerald-900"
                    >
                      <Edit2 size={16} />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleDelete(budget.id)}
                      className="h-8 w-8 p-0 text-slate-400 hover:text-red-500"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Gastado</span>
                    <span className={cn(
                      "font-mono font-medium",
                      budget.is_exceeded ? "text-red-500" : "text-slate-900"
                    )}>
                      {formatCurrency(budget.spent, user?.currency)}
                    </span>
                  </div>
                  
                  <Progress 
                    value={Math.min(budget.percentage, 100)} 
                    className={cn(
                      "h-2",
                      budget.is_exceeded && "[&>div]:bg-red-500"
                    )}
                  />
                  
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">
                      {budget.is_exceeded ? 'Excedido' : 'Disponible'}
                    </span>
                    <span className={cn(
                      "font-mono font-medium",
                      budget.is_exceeded ? "text-red-500" : "text-emerald-600"
                    )}>
                      {budget.is_exceeded 
                        ? `-${formatCurrency(budget.spent - budget.amount, user?.currency)}`
                        : formatCurrency(budget.remaining, user?.currency)
                      }
                    </span>
                  </div>

                  <div className="pt-2 border-t border-slate-100 flex justify-between items-center">
                    <span className="text-xs text-slate-400">Presupuesto</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-medium text-slate-900">
                        {formatCurrency(budget.amount, user?.currency)}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => fetchHistory(budget.id)}
                        className="h-6 w-6 p-0 text-slate-400 hover:text-emerald-900"
                        title="Ver historial"
                      >
                        <BarChart3 size={14} />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Historical Chart */}
      {historyData && (
        <Card className="border-slate-200 rounded-sm shadow-none" data-testid="budget-history-chart">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div 
                  className="w-8 h-8 rounded-sm flex items-center justify-center"
                  style={{ backgroundColor: `${historyData.category_color}15` }}
                >
                  <BarChart3 className="w-4 h-4" style={{ color: historyData.category_color }} />
                </div>
                <div>
                  <CardTitle className="text-sm font-semibold text-slate-900">
                    Historial: {historyData.category_name}
                  </CardTitle>
                  <p className="text-xs text-slate-400">
                    Últimos {historyData.history?.length} meses · Promedio: {formatCurrency(historyData.average_spent, user?.currency)}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setHistoryData(null)}
                className="text-slate-400"
              >
                Cerrar
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {historyLoading ? (
              <div className="h-64 flex items-center justify-center">
                <RefreshCw className="w-6 h-6 animate-spin text-emerald-900" />
              </div>
            ) : (
              <>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={historyData.history}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis 
                        dataKey="month_short" 
                        tick={{ fontSize: 12, fill: '#64748B' }}
                      />
                      <YAxis 
                        tick={{ fontSize: 12, fill: '#64748B' }}
                        tickFormatter={(v) => `€${v}`}
                      />
                      <Tooltip 
                        formatter={(value, name) => [formatCurrency(value, user?.currency), name === 'spent' ? 'Gastado' : 'Presupuesto']}
                        contentStyle={{ borderRadius: '2px', border: '1px solid #E2E8F0' }}
                      />
                      <Legend />
                      <Bar 
                        dataKey="spent" 
                        fill={historyData.category_color}
                        name="Gastado"
                        radius={[2, 2, 0, 0]}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="budget" 
                        stroke="#EF4444" 
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        name="Presupuesto"
                        dot={false}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
                
                {/* Summary stats */}
                <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-slate-100">
                  <div className="text-center">
                    <p className="text-xs text-slate-400">Meses excedidos</p>
                    <p className={cn(
                      "font-mono text-lg font-medium",
                      historyData.months_exceeded > 0 ? "text-red-500" : "text-emerald-600"
                    )}>
                      {historyData.months_exceeded} de {historyData.history?.length}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-slate-400">Promedio mensual</p>
                    <p className="font-mono text-lg font-medium text-slate-900">
                      {formatCurrency(historyData.average_spent, user?.currency)}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-slate-400">Presupuesto actual</p>
                    <p className="font-mono text-lg font-medium text-slate-900">
                      {formatCurrency(historyData.current_budget, user?.currency)}
                    </p>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Budget Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="budget-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editingBudget ? 'Editar Presupuesto' : 'Nuevo Presupuesto'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">
                Categoría *
              </Label>
              <Select value={form.category_id} onValueChange={(v) => setForm(prev => ({ ...prev, category_id: v }))}>
                <SelectTrigger className="rounded-sm" data-testid="budget-category-select">
                  <SelectValue placeholder="Seleccionar categoría" />
                </SelectTrigger>
                <SelectContent>
                  {availableCategories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">
                Monto Presupuestado *
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                  {user?.currency === 'EUR' ? '€' : '$'}
                </span>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={form.amount}
                  onChange={(e) => setForm(prev => ({ ...prev, amount: e.target.value }))}
                  className="pl-8 font-mono rounded-sm"
                  placeholder="0.00"
                  required
                  data-testid="budget-amount-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">
                Período
              </Label>
              <Select value={form.period} onValueChange={(v) => setForm(prev => ({ ...prev, period: v }))}>
                <SelectTrigger className="rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Semanal</SelectItem>
                  <SelectItem value="monthly">Mensual</SelectItem>
                  <SelectItem value="yearly">Anual</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setDialogOpen(false)}
                className="rounded-sm"
              >
                Cancelar
              </Button>
              <Button 
                type="submit" 
                className="bg-emerald-900 hover:bg-emerald-800 rounded-sm"
                data-testid="save-budget-btn"
              >
                Guardar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Budgets;
